/**
 * API-Client mit automatischem Token-Refresh.
 */

import { askPassword, type Bestaetigung } from './passwordConfirm';
import { getRollensicht } from './rollensicht';
import { createSession } from '../../../shared/session';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code = 'error',
    public details?: { path: string; message: string }[],
  ) {
    super(message);
  }
}

const { tokenStore, apiUrl, tryRefresh } = createSession('ff');
export { tokenStore };

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, unknown>;
  skipAuth?: boolean;
  raw?: boolean;
  /** Eigenes Passwort (`X-Confirm-Password`), ggf. mit Authenticator-Code (`X-Confirm-Totp`). */
  confirm?: Bestaetigung;
}

export async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = apiUrl(path);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    const token = tokenStore.access;
    if (token && !options.skipAuth) headers.Authorization = `Bearer ${token}`;
    // Testsicht auf eine Rolle. Sie hängt an jeder Anfrage, weil sie sonst nur
    // die halbe Anwendung beträfe – der Server schneidet damit die Rechte auf
    // die Rolle zu. Sie kann ausschließlich Rechte wegnehmen, nie geben.
    const rolle = getRollensicht();
    if (rolle && !options.skipAuth) headers['X-Rollensicht'] = String(rolle);
    if (options.confirm) headers['X-Confirm-Password'] = options.confirm.password;
    if (options.confirm?.totp) headers['X-Confirm-Totp'] = options.confirm.totp;

    return fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  };

  let response: Response;
  try {
    response = await send();
  } catch {
    // Netzfehler: der Server ist nicht zu erreichen (siehe `useUnreachable`).
    window.dispatchEvent(new Event('ff:unreachable'));
    throw new ApiError(0, 'Der Server ist gerade nicht erreichbar.', 'unreachable');
  }

  if (response.status === 401 && !options.skipAuth && (await tryRefresh())) {
    response = await send();
  }

  if (response.status === 401 && !options.skipAuth) {
    tokenStore.clear();
    window.dispatchEvent(new CustomEvent('ff:unauthorized'));
  }

  // Proxy-Fehlerseiten sind HTML, kein JSON – nicht erst parsen.
  if ([502, 503, 504].includes(response.status)) {
    window.dispatchEvent(new Event('ff:unreachable'));
    throw new ApiError(response.status, 'Der Server ist gerade nicht erreichbar.', 'unreachable');
  }

  if (options.raw) return response as unknown as T;

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  // Meldet geschriebene Änderungen (z. B. an die DSFA-Plaketten, lib/dsfa.ts).
  if (response.ok && options.method && options.method !== 'GET') window.dispatchEvent(new Event('ff:mutated'));

  if (!response.ok) {
    throw new ApiError(
      response.status,
      data?.message ?? 'Die Anfrage konnte nicht verarbeitet werden.',
      data?.error,
      data?.details,
    );
  }
  return data as T;
}

let letzte: { confirm: Bestaetigung; zeit: number } | null = null;

/**
 * Führt einen Löschvorgang mit Passwortbestätigung aus: Passwort abfragen,
 * senden, bei falscher Eingabe erneut fragen (dreimal). Abbruch wirft
 * `cancelled`, damit niemand eine Fehlermeldung für eine bewusste Absage sieht.
 * Mit `mitCode` fragt der Dialog von Anfang an auch den Code der Authenticator-App
 * ab (Export, Zurücksetzen, Sicherung, Wiederherstellung).
 */
async function withPassword<T>(send: (confirm: Bestaetigung) => Promise<T>, mitCode = false): Promise<T> {
  let hint: string | undefined;
  for (let versuch = 0; versuch < 4; versuch++) {
    // Folgt gleich auf eine bestätigte Abfrage (Export erzeugen, dann laden),
    // gilt dieselbe Bestätigung kurz weiter – der Code läuft erst nach ~30 s ab.
    const frisch = mitCode && letzte && Date.now() - letzte.zeit < 20_000 ? letzte.confirm : null;
    const confirm = frisch ?? (await askPassword(hint, mitCode));
    if (confirm === null) throw new ApiError(0, 'Abgebrochen.', 'cancelled');
    try {
      const result = await send(confirm);
      if (mitCode) letzte = { confirm, zeit: Date.now() };
      return result;
    } catch (error) {
      letzte = null;
      if (!(error instanceof ApiError)) throw error;
      // Der Server verlangt für Superadmin-Konten zusätzlich den Code der Authenticator-App.
      if (error.code === 'totp_required') mitCode = true;
      else if (error.code !== 'password_invalid' && error.code !== 'totp_invalid') throw error;
      hint = error.message;
    }
  }
  throw new ApiError(403, 'Die Bestätigung wurde zu oft falsch eingegeben.', 'password_invalid');
}

export const api = {
  get: <T = any>(path: string, query?: Record<string, unknown>) => request<T>(path, { query }),
  post: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T = any>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T = any>(path: string, query?: Record<string, unknown>) =>
    withPassword((confirm) => request<T>(path, { method: 'DELETE', query, confirm })),
  /** POST, der Daten löscht (etwa das Zurücksetzen): ebenfalls mit Passwort, mit `mitCode` auch 2FA. */
  postConfirmed: <T = any>(path: string, body?: unknown, mitCode = false) =>
    withPassword((confirm) => request<T>(path, { method: 'POST', body, confirm }), mitCode),
  /** PATCH auf eine Systemrolle: ebenfalls mit Passwort. */
  patchConfirmed: <T = any>(path: string, body?: unknown) =>
    withPassword((confirm) => request<T>(path, { method: 'PATCH', body, confirm })),
};

/**
 * Lädt eine Datei über den authentifizierten Endpunkt herunter.
 *
 * Hieß `downloadCsv`, solange nur CSV so geholt wurde. Seit die
 * Mandantenexporte als ZIP kommen, wäre der Name eine falsche Fährte – an der
 * Umsetzung war nie etwas CSV-eigenes.
 */
export async function downloadFile(path: string, filename: string, query?: Record<string, unknown>, mitCode = false) {
  const hole = async (confirm?: Bestaetigung) => {
    const antwort = await request<Response>(path, { query, raw: true, confirm });
    if (antwort.ok) return antwort;
    // `raw` wirft nicht – die Fehlerkennung (etwa `totp_required`) steht im Körper.
    const body = await antwort.json().catch(() => null);
    throw new ApiError(antwort.status, body?.message ?? 'Export fehlgeschlagen.', body?.error);
  };
  const response = mitCode ? await withPassword(hole, true) : await hole();
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

/**
 * Holt ein Bild vom authentifizierten Endpunkt als Objekt-URL.
 *
 * Ein schlichtes `<img src="/api/…">` scheitert hier: Der Browser hängt an
 * eigenständige Bildanfragen keinen Authorization-Header, und die API kennt
 * keine Cookie-Sitzung. Den Token stattdessen an die URL zu hängen, wäre der
 * kürzere Weg – er landete dann aber in Verlauf, Zwischenspeicher und
 * Server-Protokollen. Also wird das Bild wie jede andere Anfrage geladen und
 * das Ergebnis als Objekt-URL bereitgestellt.
 *
 * Der Aufrufer muss die zurückgegebene URL mit `URL.revokeObjectURL` freigeben.
 */
export async function fetchImageUrl(path: string): Promise<string | null> {
  const response = await request<Response>(path, { raw: true });
  if (!response.ok) return null;
  return URL.createObjectURL(await response.blob());
}
